<template>
  <PricingTiers
    :items="tiers"
    :currency-symbol="currencySymbol"
    :price-locale="priceLocale"
    :collapsible-max-height="collapsibleMaxHeight"
    pricing-position="top"
  />
</template>

<script setup lang="ts">
import { PricingTiers, type PricingTier } from '@ramp106/omrjs-hygraph-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ProductPricingPlanFragment } from '@/gql/reviews'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper'

const PROMOTED_LABEL_FALLBACKS = {
  recommended: 'Recommended',
  popular: 'Popular',
  top_offer: 'Top Offer',
} as const

const COLLAPSE_PERK_THRESHOLD = 10
const COLLAPSED_HEIGHT_PX = 510

const props = defineProps<{
  pricingPlans: ProductPricingPlanFragment[]
}>()

const { t, te, locale } = useI18n()

const currentLocale = computed(() => (ALLOWED_LOCALES.includes(locale.value) ? locale.value : ALLOWED_LOCALES[0]))
const priceLocale = computed(() => (currentLocale.value === 'de' ? 'de-DE' : 'en-US'))
const currencySymbol = computed(() => props.pricingPlans.find((p) => p.priceCurrencySymbol)?.priceCurrencySymbol ?? '€')

function perksFor(plan: ProductPricingPlanFragment): string[] {
  const fallback = ALLOWED_LOCALES[0]
  const value =
    plan.perksTranslations.find((tr) => tr.locale === currentLocale.value)?.value ??
    plan.perksTranslations.find((tr) => tr.locale === fallback)?.value ??
    ''
  return value
    .split('|')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
}

function recommendationLabel(plan: ProductPricingPlanFragment): string {
  const label = plan.promotedLabel
  if (!label) return ''
  const key = `Reviews.product.edit.pricing.promotedLabel.value.${label}`
  return te(key, currentLocale.value) ? t(key, {}, { locale: currentLocale.value }).toString() : PROMOTED_LABEL_FALLBACKS[label]
}

const collapsibleMaxHeight = computed(() =>
  props.pricingPlans.some((plan) => perksFor(plan).length > COLLAPSE_PERK_THRESHOLD) ? COLLAPSED_HEIGHT_PX : undefined,
)

const tiers = computed<PricingTier[]>(() =>
  props.pricingPlans.map((plan, index) => {
    const hasPrice = !plan.priceTextValue && typeof plan.priceCents === 'number'
    return {
      id: plan.id ?? `pricing-plan-${index}`,
      isRecommended: plan.promoted && plan.promotedLabel != null,
      pricingTierTitle: plan.name ?? '',
      pricingTierSubTitle: plan.description || null,
      pricingTierTitleIcon: plan.titleIcon ?? '',
      pricingTierRecommendationLabel: recommendationLabel(plan),
      featuresTitle: '',
      features: perksFor(plan).join('\n'),
      hasPrice,
      priceValue: hasPrice && plan.priceCents != null ? plan.priceCents / 100 : null,
      paidPer: plan.billingPeriod ? `/ ${plan.billingPeriod}` : null,
      pricingDescription: plan.startingPrice ? t('Reviews.product.view.pricing.startingAt').toString() : null,
      noPriceTopline: null,
      noPriceBottomline: plan.priceTextValue ?? null,
      primaryCtaLabel: null,
      primaryCtaLink: null,
      secondaryCtaLabel: null,
      secondaryCtaLink: null,
    }
  }),
)
</script>
